import { environment } from 'src/environments';
import { ListPatientTreatmentsParams } from './listPatientTreatmentsParams';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import {
  CreateTreatmentInvoice,
  DerivedTreatmentInfo,
  DownloadLink,
  DownloadLinkWithFileId,
  FileInfo,
  InProgressTreatment,
  MessageFileInfo,
  OrderDesignStatuses,
  PatientFileModel,
  PatientTreatment,
  PrintSettings,
  SaveFileInfoResult,
  Treatment,
  TreatmentFileInput,
  TreatmentFilesPageModel,
  TreatmentInfo,
  TreatmentInput,
  TreatmentMessageIdInput,
  MomentDirectOrderTransactionStatus,
  MomentDirectTreatmentInput,
  TreatmentShippingAddress,
  TreatmentShippingTrackingNumberInputModel,
  UploadLinkModel,
} from '../models';
import { BaseLoadTableResult } from './baseLoadTableResult';
import { map, Observable } from 'rxjs';
import { TreatmentReviewModel } from '../models/treatment/checkout';
import { TreatmentFileKind } from '../enums';
import { parseDate } from 'app/core/utils/parse-date';
import { CampaignPromoCodeUsages } from 'app/core/models/promo-code';
import { SUPPRESS_HTTP_ERROR_TOAST } from 'app/core/interceptors/errorInterceptor';

const basePath = `${environment.apiBaseUrl}treatment`;

@Injectable({ providedIn: 'root' })
export class TreatmentApiClient {
  private readonly _http = inject(HttpClient);

  /** Cancels a new order after Design Service confirms cancellation. */
  cancelOrder(id: string): Observable<void> {
    return this._http.patch<void>(`${basePath}/${id}/cancel-order`, null, this.getHttpOptions(true));
  }

  listPatientTreatments(params: ListPatientTreatmentsParams) {
    return this._http.post<BaseLoadTableResult<PatientTreatment>>(`${basePath}/doctor/load-page`, params);
  }

  getFileDesignStatuses(id: string): Observable<OrderDesignStatuses> {
    return this._http.get<OrderDesignStatuses>(`${basePath}/${id}/file-design-statuses`);
  }

  getInProgressTreatment(id: string, includeConversation: boolean = true) {
    return this._http
      .get<InProgressTreatment>(`${basePath}/in-progress/${id}`, {
        params: { includeConversation: String(includeConversation) },
      })
      .pipe(
        map(t => ({
          ...t,
          modifiedDate: parseDate(t.modifiedDate),
        }))
      );
  }

  getDraftInProgress(id: string): Observable<Treatment> {
    return this._http.get<Treatment>(`${basePath}/${id}/draft-in-progress`);
  }

  getInfo(id: string): Observable<TreatmentInfo> {
    return this._http.get<TreatmentInfo>(`${basePath}/${id}/info`);
  }

  saveInfo(id: string, info: TreatmentInfo, suppressHttpErrorToast = false): Observable<boolean> {
    return this._http.patch<boolean>(`${basePath}/${id}/info`, info, this.getHttpOptions(suppressHttpErrorToast));
  }

  reprintSaveInfo(id: string, info: TreatmentInfo, suppressHttpErrorToast = false): Observable<boolean> {
    return this._http.patch<boolean>(
      `${basePath}/${id}/reprint-info`,
      info,
      this.getHttpOptions(suppressHttpErrorToast)
    );
  }

  reprintDraft(id: string, info: DerivedTreatmentInfo, suppressHttpErrorToast = false): Observable<Treatment> {
    return this._http.post<Treatment>(
      `${basePath}/${id}/reprint-draft`,
      info,
      this.getHttpOptions(suppressHttpErrorToast)
    );
  }

  getPrintSettings(id: string): Observable<PrintSettings[]> {
    return this._http.get<PrintSettings[]>(`${basePath}/${id}/print-settings`);
  }

  getReviewPrice(id: string): Observable<TreatmentReviewModel> {
    return this._http.get<TreatmentReviewModel>(`${basePath}/${id}/review-price`);
  }

  getReprintReviewPrice(id: string): Observable<TreatmentReviewModel> {
    return this._http.get<TreatmentReviewModel>(`${basePath}/${id}/reprint-review-price`);
  }

  upgradeToVeneersDraft(id: string, info: DerivedTreatmentInfo): Observable<Treatment> {
    return this._http.post<Treatment>(`${basePath}/${id}/upgrade-to-veneers-draft`, info);
  }

  getUpgradeToVeneersReviewPrice(id: string): Observable<TreatmentReviewModel> {
    return this._http.get<TreatmentReviewModel>(`${basePath}/${id}/upgrade-to-veneers-review-price`);
  }

  getRedesignReviewPrice(id: string): Observable<TreatmentReviewModel> {
    return this._http.get<TreatmentReviewModel>(`${basePath}/${id}/redesign-review-price`);
  }

  save(model: TreatmentInput): Observable<Treatment> {
    return this._http.post<Treatment>(`${basePath}/save`, model);
  }

  saveShippingAddress(
    id: string,
    model: any,
    syncToApx = false,
    suppressHttpErrorToast = false
  ): Observable<TreatmentShippingAddress> {
    return this._http.post<TreatmentShippingAddress>(`${basePath}/${id}/shipping-address`, model, {
      params: { syncToApx: String(syncToApx) },
      ...this.getHttpOptions(suppressHttpErrorToast),
    });
  }

  getShippingAddressInfo(id: string): Observable<TreatmentShippingAddress> {
    return this._http.get<TreatmentShippingAddress>(`${basePath}/${id}/shipping-address`);
  }

  confirmReview(id: string, model: any): Observable<CreateTreatmentInvoice> {
    return this._http.patch<CreateTreatmentInvoice>(`${basePath}/${id}/confirm-review`, model);
  }

  pushMomentDirectTreatment(model: MomentDirectTreatmentInput): Observable<void> {
    return this._http.post<void>(`${basePath}/moment-direct/push`, model);
  }

  getMomentDirectOrderTransactionStatus(
    practiceAppointmentId: number
  ): Observable<MomentDirectOrderTransactionStatus | null> {
    return this._http.get<MomentDirectOrderTransactionStatus | null>(
      `${basePath}/moment-direct/transactions/${practiceAppointmentId}/status`
    );
  }

  getMomentDirectOrderTransactionStatuses(
    practiceAppointmentIds: number[]
  ): Observable<MomentDirectOrderTransactionStatus[]> {
    return this._http.post<MomentDirectOrderTransactionStatus[]>(
      `${basePath}/moment-direct/transactions/statuses`,
      practiceAppointmentIds
    );
  }

  getMomentDirectOrderPatientFiles(practiceAppointmentId: number): Observable<PatientFileModel[]> {
    return this._http.get<PatientFileModel[]>(
      `${basePath}/moment-direct/transactions/${practiceAppointmentId}/patient-files`
    );
  }

  confirmApprove(id: string, model: any): Observable<CreateTreatmentInvoice> {
    return this._http.patch<CreateTreatmentInvoice>(`${basePath}/${id}/confirm-approve`, model);
  }

  reprintConfirmReview(id: string, model: any): Observable<CreateTreatmentInvoice> {
    return this._http.patch<CreateTreatmentInvoice>(`${basePath}/${id}/reprint-confirm-review`, model);
  }

  upgradeToVeneersConfirmReview(id: string, model: any): Observable<CreateTreatmentInvoice> {
    return this._http.patch<CreateTreatmentInvoice>(`${basePath}/${id}/upgrade-to-veneers-confirm-review`, model);
  }

  redesignConfirmReview(id: string, model: any): Observable<CreateTreatmentInvoice> {
    return this._http.patch<CreateTreatmentInvoice>(`${basePath}/${id}/redesign-confirm-review`, model);
  }

  getMessageFiles(orderId: string, messageId: string): Observable<FileInfo[]> {
    return this._http.get<FileInfo[]>(`${basePath}/${orderId}/message-files/${messageId}`).pipe(
      map(items =>
        items.map(i => ({
          ...i,
          extension: i.extension ? i.extension : `${i.name.substring(i.name.lastIndexOf('.'))}`,
        }))
      )
    );
  }

  getAllMessageFiles(orderId: string): Observable<MessageFileInfo[]> {
    return this._http.get<MessageFileInfo[]>(`${basePath}/${orderId}/message-files`).pipe(
      map(items =>
        items.map(item => ({
          ...item,
          files: item.files.map(file => ({
            ...file,
            extension: file.extension ? file.extension : `${file.name.substring(file.name.lastIndexOf('.'))}`,
          })),
        }))
      )
    );
  }

  getKindFiles(id: string, kind: TreatmentFileKind): Observable<FileInfo[]> {
    return this._http.get<FileInfo[]>(`${basePath}/${id}/kind-files/${kind}`).pipe(
      map(items =>
        items.map(i => ({
          ...i,
          extension: i.extension ? i.extension : `${i.name.substring(i.name.lastIndexOf('.'))}`,
        }))
      )
    );
  }

  getUploadLink(id: string, filename: string, checkTreatmentStatus: boolean = true): Observable<UploadLinkModel> {
    if (checkTreatmentStatus) {
      return this._http.get<UploadLinkModel>(`${basePath}/${id}/upload-link?filename=${encodeURIComponent(filename)}`);
    } else {
      return this._http.get<UploadLinkModel>(
        `${basePath}/${id}/revision/upload-link?filename=${encodeURIComponent(filename)}`
      );
    }
  }

  getExternalTreatmentId(id: string): Observable<string> {
    return this._http.get<string>(`${basePath}/${id}/external-treatment-id`);
  }

  saveFileInfo(id: string, model: TreatmentFileInput): Observable<any> {
    return this._http.post(`${basePath}/${id}/file`, model);
  }

  saveDesignFileInfo(id: string, model: TreatmentFileInput): Observable<SaveFileInfoResult> {
    return this._http.post<SaveFileInfoResult>(`${basePath}/${id}/design-file`, model);
  }

  triggerSampleOrderDesignSimulation(
    id: string,
    model: {
      message: string;
      mockDesigner: {
        userId: string;
        name: string;
        email?: string | null;
      };
      designFiles: Array<{
        scanType: number;
        fileGuid: string;
        filename: string;
        fileSize: number;
        fileType: string;
        subGroup?: string | null;
        teeth?: string | null;
      }>;
    }
  ): Observable<void> {
    return this._http.post<void>(`${basePath}/${id}/sample-design-simulation`, model);
  }

  deleteFile(id: string, fileGuid: string, checkDraft: boolean = false): Observable<any> {
    return this._http.delete(`${basePath}/${id}/file/${fileGuid}/${checkDraft}`);
  }

  savePatientFilesPageInfo(id: string, model: any): Observable<any> {
    return this._http.post(`${basePath}/${id}/patient-files-page`, model);
  }

  getPatientFilesPageInfo(id: string): Observable<TreatmentFilesPageModel> {
    return this._http.get<TreatmentFilesPageModel>(`${basePath}/${id}/patient-files-page`);
  }

  getDownloadLinks(files: FileInfo[]): Observable<DownloadLinkWithFileId[]> {
    return this._http.post<DownloadLinkWithFileId[]>(`${basePath}/download-links`, files);
  }

  getDownloadLink(fileGuid: string, fileExtension: string, needCompressedFileLink = false) {
    let actionName = 'download-link';

    if (Number.isInteger(fileGuid)) {
      actionName = 'treatment-file-download-link';
    }

    return this._http.get<DownloadLink>(
      `${basePath}/${actionName}?fileId=${fileGuid}&fileExtension=${fileExtension}&needCompressedFileLink=${needCompressedFileLink}`
    );
  }

  setMessageId(id: string, model: TreatmentMessageIdInput) {
    return this._http.patch(`${basePath}/${id}/message-id`, model);
  }

  getUnsentFiles(id: string) {
    return this._http.get<FileInfo[]>(`${basePath}/${id}/unsent-files`);
  }

  getShippingAddress(id: string) {
    return this._http.get<TreatmentShippingAddress>(`${basePath}/${id}/shipping-address`);
  }

  delete(...treatmentIds: string[]) {
    return this._http.patch(`${basePath}/delete`, treatmentIds);
  }

  getPromoCodeUsage(): Observable<CampaignPromoCodeUsages> {
    return this._http.get<CampaignPromoCodeUsages>(`${basePath}/promo-code-usage`);
  }

  setTrackingNumber(id: string, model: TreatmentShippingTrackingNumberInputModel) {
    return this._http.post(`${basePath}/${id}/tracking-number`, model);
  }

  reopenTreatment(id: string): Observable<any> {
    return this._http.patch<any>(`${basePath}/re-open/${id}`, {});
  }

  private getHttpOptions(suppressHttpErrorToast: boolean) {
    return suppressHttpErrorToast ? { context: new HttpContext().set(SUPPRESS_HTTP_ERROR_TOAST, true) } : {};
  }
}
